import React, { useState, useEffect, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useHistory } from "react-router-dom";

import { Row, Col, Form } from "react-bootstrap";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";
import Modal from "react-bootstrap/Modal";
import ComponentWrapper from "./ComponentWrapper";
import SubmitButton from "../UIComponents/SubmitButton";
import UITrackingList from "../UIComponents/UITrackingList";
import UlPausedList from "../UIComponents/UlPausedList";
import MedicalListSelector from "../UIComponents/MedicalListSelector";
import SymptomReplacePicker from "../UIComponents/SymptomReplacePicker";

import useListSelector from "../../lib/useListSelector";

import {
  getTBISymptoms,
  addSymptomDetails,
  getPausedList,
  replaceWorstFactors,
  postMoreTrackedFactors,
} from "../../api/TBIRequests";

const AddSymptoms = ({ topSymptoms, setTopSymptoms, worstSymptoms, setWorstSymptoms, trackingMode, user }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { patient } = useContext(PatientContext);

  const tSymptom = (category, factor) => {
    const key = `symptom.${category}.${(factor || "").toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60)}`;
    const translated = t(key);
    return translated !== key ? translated : factor;
  };

  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [topSymptomList, setTopSymptomList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [headacheParts, setHeadacheParts] = useState([]);
  const [showErrorMsg, setShowErrorMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pauseList, setPauseList] = useState([]);
  const [pausedFactorObj, setPausedFactorObj] = useState([]);
  const [pendingNewSymptoms, setPendingNewSymptoms] = useState([]);
  const [replacementPairs, setReplacementPairs] = useState([]);
  const [availableTop3, setAvailableTop3] = useState([]);
  const [replaceStepIndex, setReplaceStepIndex] = useState(0);
  const [showReplacePicker, setShowReplacePicker] = useState(false);
  // Only used when trackingMode is null (not actively mid-session) — asks
  // whether the new symptom(s) belong in the top-3 or the total list.
  const [showListChoice, setShowListChoice] = useState(false);

  const {
    toggleFromSelectedList,
    switchSingleSelection,
    addOther,
    selectedData,
    initialize,
  } = useListSelector({
    setInitialData: setReturnedData,
    initialData: returnedData,
  });

  const messages = {
    cognitive: `${t("addsymptoms.category.cognitive")}: `,
    sleep: `${t("addsymptoms.category.sleep")}: `,
    physical: `${t("addsymptoms.category.physical")}: `,
    emotional: `${t("addsymptoms.category.emotional")}: `,
    speech: `${t("addsymptoms.category.speech")}: `,
    vision: `${t("addsymptoms.category.vision")}: `,
  };

  const tabs = {
    cognitive: t("addsymptoms.category.cognitive"),
    sleep: t("addsymptoms.category.sleep"),
    physical: t("addsymptoms.category.physical"),
    emotional: t("addsymptoms.category.emotional"),
    speech: t("addsymptoms.category.speech"),
    vision: t("addsymptoms.category.vision"),
  };

  const colors = {
    cognitive: "#edebf3",
    sleep: "#ffdadb",
    physical: "#ffdcaf",
    emotional: "#fff3c4",
    speech: "#cef3c4",
    vision: "#c1fdf7",
  };

  const colorsSelected = {
    cognitive: "#dab7ec",
    sleep: "#ffdadb",
    physical: "#ffdcaf",
    emotional: "#fff3c4",
    speech: "#cef3c4",
    vision: "#c1fdf7",
  };

  useEffect(() => {
    let isMounted = true; // Track whether the component is mounted

    getPausedList({
      patientId: patient?.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
    })
      .then(({ data }) => {
        if (isMounted) {
          const li = [];
          const pausedObj = [];
          data.forEach((i) => {
            if (!li.includes(i.factor)) {
              li.push(i.factor);
              pausedObj.push(i);
            }
          });
          setPauseList(li);
          setPausedFactorObj(pausedObj);
        }
      })
      .catch(() => history.push("/oops"));

    // Cleanup function
    return () => {
      isMounted = false; // Set isMounted to false when the component is unmounted
    };
  }, []);

  const changeErrorMsg = (errorCase) => {
    const errorMsgList = {
      noSymptom: t("addsymptoms.error.no_symptom"),
      needHeadAcheLocation: t("addsymptoms.error.headache_location"),
      onlyOneAtATime: t("addsymptoms.error.only_one_at_a_time"),
      tooManySelected: t("addsymptoms.error.too_many_selected"),
      noWorstSymptoms: t("addsymptoms.error.no_worst_symptoms"),
    };
    setErrorMsg(errorMsgList[errorCase]);
    setShowErrorMsg(true);
  };

  const getUntrackingSymptomsObj = (list) => {
    const unTrackingList = [];
    list.forEach((symptom) => {
      if (
        !topSymptomList.includes(symptom.factor) &&
        symptom.category !== "SDOH"
      ) {
        unTrackingList.push(symptom);
      }
    });
    return unTrackingList;
  };

  const categories = [
    "cognitive",
    "sleep",
    "emotional",
    "physical",
    "speech",
    "vision",
  ];

  const createItemListFromRes = (data) => {
    return categories.map((category) => {
      const items = data
        .filter((item) => item.subcategory === category)
        .map((item) => item.factor);
      return { category, items };
    });
  };

  const buildMoreSymptoms = async () => {
    try {
      getTBISymptoms().then(({ data }) => {
        const availableSymptoms = getUntrackingSymptomsObj(data);
        const itemList = createItemListFromRes(availableSymptoms);

        initialize(itemList);
        setReturnedData(itemList);
      });
      setIsAdding(true);
    } catch {
      history.push("/oops");
    }
  };

  const handleClose = () => {
    setIsAdded(true);
  };

  const headacheFactor = "Headaches caused by close work: computers, reading, gaming";

  const includesHeadacheCheck = (list) =>
    list.some((s) => s.factor === headacheFactor);

  // "Track All Symptoms" path — the total list has no fixed size, so new
  // symptoms are simply appended, no replacement/picker needed.
  const applyPlainAdd = (newSymptoms, includesHeadache) => {
    setSubmitting(true);

    if (includesHeadache && headacheParts.length > 0) {
      addSymptomDetails({
        patientId: patient?.patientId,
        data: { symptom: headacheFactor, detail: headacheParts },
      });
    }

    const processedData = newSymptoms.map((s) => ({
      factor: s.factor,
      category: s.category,
      subcategory: s.subcategory,
    }));

    postMoreTrackedFactors({
      patientId: patient?.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
      data: processedData,
    })
      .then(() => {
        setTopSymptoms((prev) => {
          const existingFactors = new Set(prev.map((s) => s.factor));
          const toAdd = newSymptoms.filter((s) => !existingFactors.has(s.factor));
          return [...prev, ...toAdd];
        });
        setIsAdded(true);
      })
      .catch(() => history.push("/oops"))
      .finally(() => {
        setSubmitting(false);
        setPendingNewSymptoms([]);
      });
  };

  // "Track Top 3 Worst Symptoms" path — extracted so it can be triggered
  // either directly (trackingMode already known) or after the list-choice
  // modal (trackingMode was null).
  const routeTop3Flow = (newSymptoms, includesHeadache) => {
    if (!worstSymptoms) {
      console.error(
        "[AddSymptoms] worstSymptoms prop is undefined — check that Dashboard.js/App.js pass worstSymptoms down to AddSymptoms."
      );
      changeErrorMsg("noWorstSymptoms");
      return;
    }

    if (newSymptoms.length === 3) {
      const pairs = worstSymptoms.map((oldSymptom, i) => ({
        oldFactor: oldSymptom.factor,
        newSymptom: newSymptoms[i],
      }));
      applyReplacements(pairs, newSymptoms, includesHeadache);
    } else {
      setReplacementPairs([]);
      setAvailableTop3(worstSymptoms);
      setReplaceStepIndex(0);
      setShowReplacePicker(true);
    }
  };

  // Applies the final set of {oldFactor, newSymptom} pairs to the backend
  // and syncs local topSymptoms/worstSymptoms state.
  const applyReplacements = (pairs, newSymptoms, includesHeadache) => {
    setSubmitting(true);

    if (includesHeadache && headacheParts.length > 0) {
      addSymptomDetails({
        patientId: patient?.patientId,
        data: { symptom: headacheFactor, detail: headacheParts },
      });
    }

    replaceWorstFactors({
      patientId: patient?.patientId,
      data: { pairs },
    })
      .then(() => {
        setTopSymptoms((prev) => {
          const existingFactors = new Set(prev.map((s) => s.factor));
          const toAdd = newSymptoms.filter((s) => !existingFactors.has(s.factor));
          return [...prev, ...toAdd];
        });
        setWorstSymptoms((prev) => {
          let updated = [...prev];
          pairs.forEach(({ oldFactor, newSymptom }) => {
            updated = updated.map((s) => (s.factor === oldFactor ? newSymptom : s));
          });
          return updated;
        });
        setIsAdded(true);
      })
      .catch(() => history.push("/oops"))
      .finally(() => {
        setSubmitting(false);
        setPendingNewSymptoms([]);
        setReplacementPairs([]);
        setAvailableTop3([]);
        setReplaceStepIndex(0);
      });
  };

  // Step 1: validate 1-3 selected symptoms. Then route based on trackingMode:
  // - "top3": skip straight to the top-3 replace flow (exactly 3 selected ->
  //    auto-pair by position, skip the picker; otherwise sequential picker).
  // - "all": skip straight to a plain add — no replacement, no cap.
  // - null (not actively mid-session today): ask which list this belongs to.
  const handleSubmit = () => {
    const selectedSymptomArr = [];
    const sArr = Object.entries(selectedData);
    for (let i = 0; i < sArr.length; i++) {
      const subcategory = sArr[i][0];
      for (let j = 0; j < sArr[i][1].length; j++) {
        selectedSymptomArr.push([subcategory, sArr[i][1][j]]);
      }
    }

    if (selectedSymptomArr.length === 0) {
      changeErrorMsg("noSymptom");
      return;
    }

    // useListSelector's toggle/max logic only caps selections within a single
    // category tab, not across all six tabs — enforce the true overall cap here.
    if (selectedSymptomArr.length > 3) {
      changeErrorMsg("tooManySelected");
      return;
    }

    const includesHeadache = selectedSymptomArr.some(([, factor]) => factor === headacheFactor);
    if (includesHeadache && headacheParts.length === 0) {
      changeErrorMsg("needHeadAcheLocation");
      return;
    }

    const newSymptoms = selectedSymptomArr.map(([subcategory, factor]) => ({
      factor,
      category: "medical",
      subcategory,
    }));

    setShowErrorMsg(false);
    setPendingNewSymptoms(newSymptoms);

    if (trackingMode === "top3") {
      routeTop3Flow(newSymptoms, includesHeadache);
    } else if (trackingMode === "all") {
      applyPlainAdd(newSymptoms, includesHeadache);
    } else {
      // Not actively in today's tracking session — ask which list this
      // symptom should go into instead of assuming top-3.
      setShowListChoice(true);
    }
  };

  // Step 2 (only reached for 1 or 2 new symptoms): user picks which top-3
  // slot the current new symptom replaces. Repeats until every new symptom
  // has been assigned a slot, then applies everything at once.
  const handleReplaceConfirm = (chosenSlot) => {
    const newSymptom = pendingNewSymptoms[replaceStepIndex];
    const updatedPairs = [...replacementPairs, { oldFactor: chosenSlot.factor, newSymptom }];
    const remainingTop3 = availableTop3.filter((s) => s.factor !== chosenSlot.factor);
    const nextStep = replaceStepIndex + 1;

    if (nextStep < pendingNewSymptoms.length) {
      setReplacementPairs(updatedPairs);
      setAvailableTop3(remainingTop3);
      setReplaceStepIndex(nextStep);
    } else {
      setShowReplacePicker(false);
      applyReplacements(updatedPairs, pendingNewSymptoms, includesHeadacheCheck(pendingNewSymptoms));
    }
  };

  useEffect(() => {
    if (topSymptoms) {
      let topList = topSymptoms.map((symptom) => symptom.factor);
      setTopSymptomList(topList);
    }
  }, [topSymptoms]);

  return (
    <ComponentWrapper>
      {isAdded ? (
        <Row className="d-flex align-items-center justify-content-center">
          <div className="d-flex justify-content-center">
            <h6
              className="pt-2 pb-2 pl-3 pr-3"
              style={{
                width: "100%",
                borderRadius: "20px",
                backgroundColor: "#3EB1A6",
                color: "white",
                textAlign: "center",
                overflowWrap: "break-word",
                cursor: "pointer",
              }}
              onClick={() => {
                setIsAdded(false);
                buildMoreSymptoms();
              }}
            >
              {t("addsymptoms.add_more_symptoms")}
            </h6>
          </div>
        </Row>
      ) : isAdding ? (
        <>
          <Row>
            <Col sm={12}>
              <UITrackingList
                symptoms={topSymptoms}
                pauseList={pauseList}
                setPauseList={setPauseList}
                pausedFactorObj={pausedFactorObj}
                setPausedFactorObj={setPausedFactorObj}
                setTopSymptoms={setTopSymptoms}
                worstSymptoms={worstSymptoms}
                setWorstSymptoms={setWorstSymptoms}
                title={t("addsymptoms.currently_tracking")}
                user={user}
              />
            </Col>
            <Col sm={12}>
              <UlPausedList
                symptoms={pauseList}
                pausedFactorObj={pausedFactorObj}
                setPausedFactorObj={setPausedFactorObj}
                setPauseList={setPauseList}
                setTopSymptoms={setTopSymptoms}
                topSymptomsList={topSymptoms}
                title={t("addsymptoms.symptom_paused")}
                user={user}
              />
            </Col>
          </Row>
          <Row className="d-flex align-items-center justify-content-center mt-4 mb-4 mr-3">
            <Col md={10}>
              <div className="bubbleText">
                {t("addsymptoms.unpausing.0")}
                <br />
                <br />
                {t("addsymptoms.unpausing.1")}
              </div>
            </Col>
            <Col md={2}>
              <SubmitButton disabled={false} onClick={handleClose}>
                {t("addsymptoms.close")}
              </SubmitButton>
            </Col>
          </Row>
          <Col sm={12}>
            <h6 style={{ textAlign: "center" }}>
              {t("addsymptoms.symptoms")}
            </h6>
          </Col>
          <Tab.Container id="left-tabs-example" defaultActiveKey="cognitive">
            <Row
              style={{
                marginTop: "0.5rem",
                fontWeight: "normal",
                fontSize: "15px",
              }}
            >
              <Col sm={3} style={{ paddingRight: "0px" }}>
                <Nav fill justify variant="tabs" className="flex-column">
                  {returnedData.map((itemList, i) => (
                    <Nav.Item key={i}>
                      <Nav.Link
                        className="navLink"
                        eventKey={itemList.category}
                        style={{
                          background: `${colors[itemList.category]}`,
                          border: "1px solid #106995",
                        }}
                      >
                        {" "}
                        {tabs[itemList.category]}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Col>
              <Col sm={5} style={{ paddingRight: "0px" }}>
                <Tab.Content>
                  {returnedData.map((itemList, i) => (
                    <Tab.Pane eventKey={itemList.category} key={i}>
                      <MedicalListSelector
                        addSymptoms={true}
                        patient={patient}
                        category={itemList.category}
                        message={messages[itemList.category]}
                        list={itemList.items}
                        index={i}
                        selectedList={selectedData[itemList.category]}
                        toggleFromSelectedList={toggleFromSelectedList}
                        selectOne={switchSingleSelection}
                        addOther={addOther}
                        setHeadacheParts={setHeadacheParts}
                        headacheParts={headacheParts}
                      />
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
              <Col sm={4}>
                <div
                  className="justify-content-left choseList"
                  style={{
                    height: "30rem",
                    overflow: "scroll",
                    color: "black",
                    border: "1px solid #106995",
                    textAlign: "left",
                    background: "#fff",
                    padding: "1rem",
                    borderRadius: "5px",
                    margin: ".25rem 0",
                  }}
                >
                  {Object.entries(selectedData).flatMap(
                    ([category, itemList]) => {
                      return itemList.map((item, i) => (
                        <div
                          className="flex items-start gap-2 my-2"
                          key={`${category}-${i}`}
                        >
                          <div className="flex-shrink-0">
                            <svg
                              width="24px"
                              height="24px"
                              viewBox="0 0 24 24"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                            >
                              <title>icon/action/check_ccircle_24px</title>
                              <defs>
                                <path
                                  d="M12,2 C6.48,2 2,6.48 2,12 C2,17.52 6.48,22 12,22 C17.52,22 22,17.52 22,12 C22,6.48 17.52,2 12,2 Z M10,17 L5,12 L6.41,10.59 L10,14.17 L17.59,6.58 L19,8 L10,17 Z"
                                  id="path-1"
                                ></path>
                              </defs>
                              <g
                                id="icon/action/check_ccircle_24px"
                                stroke="none"
                                strokeWidth="1"
                                fill="none"
                                fillRule="evenodd"
                              >
                                <mask id="mask-2" fill="white">
                                  <use xlinkHref="#path-1"></use>
                                </mask>
                                <use
                                  id="-↳Color"
                                  fill={colorsSelected[category]}
                                  xlinkHref="#path-1"
                                ></use>
                              </g>
                            </svg>{" "}
                          </div>
                          <span>{tSymptom(category, item)}</span>
                        </div>
                      ));
                    }
                  )}
                </div>
              </Col>
            </Row>
          </Tab.Container>
          <Row>
            {showErrorMsg && (
              <Form.Control.Feedback
                style={{ display: "block" }}
                type="invalid"
              >
                {errorMsg}
              </Form.Control.Feedback>
            )}
          </Row>
          <SubmitButton disabled={submitting} onClick={handleSubmit}>
            {t("addsymptoms.save_and_add")}
          </SubmitButton>

          <SymptomReplacePicker
            show={showReplacePicker}
            title={`${t("addsymptoms.choose_replacement")}${
              pendingNewSymptoms[replaceStepIndex]
                ? ": " + tSymptom(
                    pendingNewSymptoms[replaceStepIndex].subcategory,
                    pendingNewSymptoms[replaceStepIndex].factor
                  )
                : ""
            }`}
            symptoms={availableTop3}
            onConfirm={handleReplaceConfirm}
            onCancel={() => {
              setShowReplacePicker(false);
              setPendingNewSymptoms([]);
              setReplacementPairs([]);
              setAvailableTop3([]);
              setReplaceStepIndex(0);
              setSubmitting(false);
            }}
          />

          {/* Only shown when trackingMode is null (not actively mid-session
              today) — trackingMode === "top3"/"all" skips straight to the
              matching flow above without asking. */}
          <Modal
            show={showListChoice}
            onHide={() => {
              setShowListChoice(false);
              setPendingNewSymptoms([]);
            }}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: "18px" }}>
                {t("addsymptoms.choose_list_title")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex justify-content-center" style={{ gap: "1rem" }}>
              <SubmitButton
                onClick={() => {
                  setShowListChoice(false);
                  routeTop3Flow(pendingNewSymptoms, includesHeadacheCheck(pendingNewSymptoms));
                }}
              >
                {t("symptomtracker.mode.top3")}
              </SubmitButton>
              <SubmitButton
                onClick={() => {
                  setShowListChoice(false);
                  applyPlainAdd(pendingNewSymptoms, includesHeadacheCheck(pendingNewSymptoms));
                }}
              >
                {t("symptomtracker.mode.all")}
              </SubmitButton>
            </Modal.Body>
          </Modal>
        </>
      ) : (
        <Row className="d-flex align-items-center justify-content-center">
          <div className="d-flex justify-content-center">
            <h6
              className="pt-2 pb-2 pl-3 pr-3"
              style={{
                width: "100%",
                borderRadius: "20px",
                backgroundColor: "#3EB1A6",
                color: "white",
                textAlign: "center",
                overflowWrap: "break-word",
                cursor: "pointer",
              }}
              onClick={buildMoreSymptoms}
            >
              {t("addsymptoms.add_symptoms")}
            </h6>
          </div>
        </Row>
      )}
    </ComponentWrapper>
  );
};

export default AddSymptoms;